import { useEffect, useState } from "react";

import { useLingui } from "@lingui/react/macro";
import {
  DpaBlock,
  LEGAL_CONTACT_EMAIL,
  LEGAL_LAST_UPDATED,
  LEGAL_NAV_KEYS,
  LEGAL_SEGMENTS,
  getLegalT,
  isLegalSegment,
  loadLegalLocale,
  renderLegalSection,
  tLocal,
  type LegalSegment,
} from "@pontimed/ui-legal";

import { TabBar, type TabItem } from "@/components/ui/tab-bar";
import { SwipeableTabsContent } from "@/components/ui/swipeable-tabs-content";
import { useCountryLegalInfo } from "@/lib/legal/use-country-legal-info";
import { useLegalCountry } from "@/lib/legal/use-legal-country";
import { buildWebUrl } from "@/lib/web-url";

/**
 * Portal-side legal body — pure controlled component. Renders tabs +
 * swipeable section carousel keyed off `activeSegment`. No URL navigation,
 * no sticky chrome, no back button: parent (LegalSheetProvider) wraps this
 * in a ResponsiveSheet which owns close + chrome.
 *
 * Country cascade still resolved via `useLegalCountry()` (URL → entity →
 * browsing → default) and DPA `legalInfo` fetched via `/api/countries/:code`.
 */
export function LegalShell({
  activeSegment,
  onSegmentChange,
}: {
  activeSegment: LegalSegment;
  onSegmentChange: (segment: LegalSegment) => void;
}) {
  const { i18n } = useLingui();
  const country = useLegalCountry();
  const locale = i18n.locale;

  const [catalogReady, setCatalogReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setCatalogReady(false);
    void loadLegalLocale(locale).then(() => {
      if (!cancelled) setCatalogReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const t = getLegalT(locale);
  const { data: countryDetail } = useCountryLegalInfo(country);
  const legalInfo = countryDetail?.legalInfo ?? null;
  const localCountryName = tLocal(countryDetail?.name, locale);
  const countryFlag = countryDetail?.flagEmoji ?? "";

  return (
    <div className="flex flex-col">
      <div className="bg-background/80 border-border/50 sticky top-0 z-10 -mx-4 border-b px-4 backdrop-blur-lg sm:-mx-6 sm:px-6">
        <TabBar
          tabs={LEGAL_SEGMENTS.map(
            (seg): TabItem<LegalSegment> => ({
              id: seg,
              label: t(`nav.${LEGAL_NAV_KEYS[seg]}`),
            }),
          )}
          activeTab={activeSegment}
          onTabChange={onSegmentChange}
          sticky={false}
        />
      </div>

      <div className="pt-6">
        <h2 className="text-foreground text-xl font-semibold sm:text-2xl">
          {t("title")}
          {localCountryName && (
            <span className="text-muted-foreground font-normal">
              {" "}
              — {countryFlag} {localCountryName}
            </span>
          )}
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">{t("subtitle")}</p>
      </div>

      <div className="flex-1 py-6">
        {catalogReady && (
          <SwipeableTabsContent
            activeTab={activeSegment}
            tabIds={LEGAL_SEGMENTS}
            onTabChange={onSegmentChange}
            renderPanel={(id) =>
              isLegalSegment(id) ? (
                <div className="prose prose-sm dark:prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground max-w-none pb-8">
                  {renderLegalSection(id, t, {
                    // The DSA form lives on www; without this the package
                    // default (bare pontimed.app/legal/…) lets www pick the
                    // language and country from its own cookies.
                    links: { reportContent: buildWebUrl("legal/report-content", locale, country) },
                  })}

                  {legalInfo && (
                    <DpaBlock
                      t={t}
                      legalInfo={legalInfo}
                      countryName={localCountryName}
                      locale={locale}
                    />
                  )}

                  <div className="border-border mt-10 border-t pt-5">
                    <p className="text-muted-foreground text-xs">
                      {t("lastUpdated", { date: LEGAL_LAST_UPDATED })}
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {t("contactDpo")}{" "}
                      <a
                        href={`mailto:${LEGAL_CONTACT_EMAIL}`}
                        className="text-primary hover:underline"
                      >
                        {LEGAL_CONTACT_EMAIL}
                      </a>
                    </p>
                  </div>
                </div>
              ) : null
            }
          />
        )}
      </div>
    </div>
  );
}
